import styled from "@emotion/styled";
import { yupResolver } from "@hookform/resolvers/yup";
import { Close, Delete } from "@mui/icons-material";
import {
  Alert,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography
} from "@mui/material";
import { Button } from "components/UI";
import { DateField } from "components/UI/InputFields/DateField";
import { MutationError } from "components/UI/MutationError";
import { useChurchStore } from "features/churches";
import { useMakeRevenueVoid } from "features/finances";
import moment from "moment";
import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const Label = styled.label`
  color: #5b5b5b;
  font-size: 12px;
  font-weight: 500;
`;

const Input = styled.input`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
`;

const ErrorText = styled.span`
  color: #d32f2f;
  font-size: 10px;
`;

const Filename = styled.span`
  font-size: 10px;
`;

const DropzoneContainer = styled.div`
  background-color: rgba(0, 0, 0, 0.05);
  border: 1px dashed #b3b3b3;
  border-radius: 4px;
  padding: 16px;
  text-align: center;
  cursor: pointer;

  &:hover {
    border-color: #3dadb7;
  }
`;

const DropzoneText = styled.p`
  margin: 0;
  font-size: 14px;
  color: #5b5b5b;
`;

const StyledDateField = styled(DateField)`
  & .MuiOutlinedInput-root {
    border-radius: 4px;
    border: 0.73px solid #b3b3b3;
    height: 40px;
    font-size: 14px;
    background-color: transparent;
  }

  & .MuiOutlinedInput-notchedOutline {
    border: none;
  }

  & .MuiInputBase-input {
    padding: 8px;
  }

  &.Mui-focused .MuiOutlinedInput-root {
    border-color: #3dadb7;
  }
`;

const schema = yup.object().shape({
  voided_at: yup.string().required("Date and time is required"),
  void_reason: yup.string().required("Reason is required")
});

export function MakeReveneVoidModal({ open, onClose, revenueId }) {
  const [attachments, setAttachments] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const selectedChurch = useChurchStore(state => state.selectedChurch);

  const makeRevenueVoidMutation = useMakeRevenueVoid();

  const onDrop = useCallback(acceptedFiles => {
    setAttachments(prev => [...prev, ...acceptedFiles]);
  }, []);

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    onDrop,
    accept: {
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
      "application/pdf": [".pdf"]
    },
    maxSize: 2 * 1024 * 1024, // 2 MB
    multiple: true
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset
  } = useForm({
    defaultValues: {
      voided_at: "",
      void_reason: ""
    },
    resolver: yupResolver(schema)
  });

  const onSubmit = data => {
    const formData = new FormData();

    // Append form fields
    Object.keys(data).forEach(key => {
      if (key === "voided_at") {
        formData.append(key, moment(data[key]).format("YYYY-MM-DDTHH:mm"));
      } else {
        formData.append(key, data[key]);
      }
    });

    // Append new attachments
    if (attachments.length > 0) {
      attachments.forEach((file, index) => {
        formData.append(`void_attachments[${index}]`, file);
      });
    } else {
      formData.set("void_attachments", []);
    }

    // Append existing attachments that weren't deleted
    if (existingAttachments.length > 0) {
      existingAttachments.forEach((filename, index) => {
        formData.append(`void_attachments[${index}]`, filename);
      });
    } else {
      formData.set("void_attachments", []);
    }

    makeRevenueVoidMutation.mutate(
      {
        churchId: selectedChurch?.id,
        revenueId: revenueId,
        voidData: formData
      },
      {
        onSuccess: () => {
          reset();
          onClose();
        }
      }
    );
  };

  const removeAttachment = index => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingAttachment = index => {
    setExistingAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleClose = () => {
    reset();
    setAttachments([]);
    setExistingAttachments([]);
    makeRevenueVoidMutation.reset();
    onClose();
  };

  return (
    <Dialog fullWidth maxWidth="xs" open={open} onClose={handleClose}>
      <DialogTitle sx={{ bgcolor: "primary.main" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="subtitle1" color="primary.contrastText">
            Void payment
          </Typography>
          <IconButton onClick={handleClose}>
            <Close sx={{ color: "primary.contrastText" }} />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Stack p={2} spacing={2}>
          <Field>
            <Label htmlFor="voided_at">Date and Time</Label>
            <Controller
              name="voided_at"
              control={control}
              render={({ field }) => (
                <StyledDateField
                  id="voided_at"
                  value={field.value}
                  onChange={field.onChange}
                  inputRef={field.ref}
                />
              )}
            />
            {errors.voided_at && (
              <ErrorText>{errors.voided_at.message}</ErrorText>
            )}
          </Field>
          <Field>
            <Label>Attachments</Label>
            {existingAttachments.length > 0 && (
              <Stack spacing={1} sx={{ mb: 1 }}>
                {existingAttachments.map((filename, index) => (
                  <Chip
                    key={index}
                    label={filename}
                    onDelete={() => removeExistingAttachment(index)}
                  />
                ))}
              </Stack>
            )}
            <DropzoneContainer {...getRootProps()}>
              <input {...getInputProps()} />
              <DropzoneText>
                Drop files (photo, pdf) here or click to upload
              </DropzoneText>
            </DropzoneContainer>
            {attachments.length > 0 && (
              <Stack spacing={1} sx={{ mt: 1 }}>
                {attachments.map((file, index) => (
                  <Stack
                    key={index}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                  >
                    <Filename>{file.name}</Filename>
                    <IconButton
                      size="small"
                      onClick={() => removeAttachment(index)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
            )}

            {fileRejections.length > 0 && (
              <Alert severity="error">
                {fileRejections
                  .map(({ errors }) =>
                    errors.map(error => {
                      if (error.code === "file-too-large") {
                        return "File size is too large. Maximum size is 2 MB.";
                      }
                      if (error.code === "file-invalid-type") {
                        return "Invalid file type. Please upload PNG, JPG or PDF files only.";
                      }
                      return error.message;
                    })
                  )
                  .join(", ")}
              </Alert>
            )}
          </Field>
          <Field>
            <Label htmlFor="void_reason">Reason</Label>
            <Input id="void_reason" {...register("void_reason")} />
            {errors.void_reason && (
              <ErrorText>{errors.void_reason.message}</ErrorText>
            )}
          </Field>

          <MutationError error={makeRevenueVoidMutation.error} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          flavor="text"
          onClick={handleClose}
          disabled={makeRevenueVoidMutation.isLoading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          disabled={makeRevenueVoidMutation.isLoading}
        >
          Void
        </Button>
      </DialogActions>
    </Dialog>
  );
}
